/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { AppState } from "Dataflows/Reducers";
import { IDistributionGroupMember } from "Interfaces/DistributionLists/IDistributionGroupMember";
import { IDistributionListsJoinRequest } from "Interfaces/DistributionLists/IDistributionListsJoinRequest";

/**
 * Returns current value from state for: distribution lists.
 * @param {AppState} state
 */
export const getDistributionLists = (state: AppState): IDistributionListsJoinRequest[] =>
  state.distributionListsState.distributionLists;

/**
 * Returns the current network operation status.
 * @param {AppState} state
 */
export const getNetworkOperationStatus = (state: AppState): OperationStatus =>
  state.distributionListsState.networkOperationStatus;

/**
 * Returns the current network operation status when approving or declining.
 * @param {AppState} state
 */
export const getNetworkOperationStatusApprove = (state: AppState): OperationStatus =>
  state.distributionListsState.networkOperationStatusApprove;

/**
 * Returns the current network operation status error.
 * @param {AppState} state
 */
export const getNetworkOperationStatusError = (state: AppState): string =>
  state.distributionListsState.networkOperationError;

/**
 * Returns the current network operation status error when approving.
 * @param {AppState} state
 */
export const getNetworkOperationStatusApproveError = (state: AppState): string =>
  state.distributionListsState.networkOperationApproveError;

/**
 * Returns current value from state for: currentPage.
 * @param {AppState} state
 */
export const getCurrentPage = (state: AppState): number => state.distributionListsState.currentPage;

/**
 * Returns current value from state for: pageSize.
 * @param {AppState} state
 */
export const getPageSize = (state: AppState): number => state.distributionListsState.pageSize;

/**
 * Returns current value from state for: currentTenant.
 * @param {AppState} state
 */
export const getCurrentTenant = (state: AppState): TenantTypesEnum | undefined =>
  state.distributionListsState.currentTenant;

/**
 * Returns current value from state for: filteredCount.
 * @param {AppState} state
 */
export const getFilteredCount = (state: AppState): number =>
  state.distributionListsState.filteredCount;

/**
 * Returns the list of distribution lists for a tenant.
 * @param {AppState} state
 */
export const getDistributionListsByTenant = (state: AppState): any =>
  state.distributionListsState.distributionListsByTenant;

/**
 * Returns the total number of DLs for that tenant.
 * @param {AppState} state
 * @returns {number} distributionListsByTenantCount
 */
export const getDistributionListsByTenantCount = (state: AppState): number =>
  state.distributionListsState.distributionListsByTenantCount;

/**
 * Returns distribution list that the user has selected.
 * @param {AppState} state
 * @returns
 */
export const getSelectedDistributionList = (state: AppState): any =>
  state.distributionListsState.selectedDistributionList;

/**
 * Returns the list of microsoft members for a distribution list.
 * @param {AppState} state
 * @returns {IDistributionGroupMember[]}
 */
export const getSelectedDistributionListMicrosoftMembers = (
  state: AppState
): IDistributionGroupMember[] =>
  state.distributionListsState.selectedDistributionListMicrosoftMembers;

/**
 * Returns the networl operation status when adding a member to a DL.
 * @param {AppState} state
 * @returns
 */
export const getNetworkOperationStatusAddMember = (state: AppState): OperationStatus =>
  state.distributionListsState.networkOperationStatusAddMember;

/**
 * Returns the networl operation status when removing a member from a DL.
 * @param {AppState} state
 * @returns
 */
export const getNetworkOperationStatusRemoveMember = (state: AppState): OperationStatus =>
  state.distributionListsState.networkOperationStatusRemoveMember;

export const getAddMemberToDLErrorMessage = (state: AppState): string =>
  state.distributionListsState.addMemberToDLErrorMessage;

export const getRemoveMemberFromDLErrorMessage = (state: AppState): string =>
  state.distributionListsState.removeMemberFromDLErrorMessage;

export const getNetworkOperationStatusFetchMicrosoftMembers = (state: AppState): string =>
  state.distributionListsState.networkOperationStatusFetchMicrosoftMembers;

export const getNetworkOperationStatusFetchByTenant = (state: AppState): string =>
  state.distributionListsState.networkOperationStatusFetchByTenant;

export const getFetchDistributionListsByTenantError = (state: AppState): string =>
  state.distributionListsState.fetchDistributionListsByTenantError;
